import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

/**
 * CustomTags - A reusable tags autocomplete component
 *
 * @param {Array} value - Current tags array (array of strings)
 * @param {Function} onChange - Callback when tags change: (event, newTags) => void
 * @param {Array} options - Available tag options (can be strings or objects with {tag, count})
 * @param {boolean} loading - Whether tags are loading
 * @param {string} label - Label for the TextField
 * @param {string} placeholder - Placeholder text
 * @param {string} helperText - Helper text
 * @param {boolean} error - Whether to show error state
 * @param {string} variant - TextField variant ("outlined", "standard", "filled")
 * @param {string} margin - TextField margin ("dense", "normal", "none")
 * @param {Function} onOpenTagsDialog - Optional callback to open tags dialog (shows button if provided)
 * @param {Object} sx - Additional sx styles
 * @param {boolean} fullWidth - Whether to use full width
 * @param {boolean} disabled - Disable input
 */
export default function CustomTags({
    value = [],
    onChange,
    options = [],
    loading = false,
    disabled = false,
    label = "Tags",
    placeholder = "Add tags (press Enter)",
    helperText = "Tags help categorize puzzles, e.g., book, comic, children.",
    error = false,
    variant = "outlined",
    margin = "dense",
    onOpenTagsDialog = null,
    sx = {},
    fullWidth = true,
}) {
    const handleChange = (event, newValue) => {
        // Convert to strings, trim, and filter out empty tags
        const processedTags = newValue
            .map((tag) => {
                // Extract tag string from object or use string directly
                const tagString = typeof tag === "string" ? tag : tag.tag || String(tag);
                const trimmed = tagString.trim();
                // Capitalize first letter
                if (trimmed.length > 0) {
                    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
                }
                return trimmed;
            })
            .filter((tag) => tag.length > 0);

        // Filter out duplicates (case-insensitive)
        const uniqueTags = processedTags.filter((tag, index, self) => {
            const normalizedTag = tag.toLowerCase();
            return self.findIndex((t) => t.toLowerCase() === normalizedTag) === index;
        });

        onChange(event, uniqueTags);
    };

    const getOptionLabel = (option) => {
        // Handle both string and object formats
        if (typeof option === "string") {
            return option;
        }
        return option.tag || String(option);
    };

    return (
        <Autocomplete
            multiple
            freeSolo
            options={options}
            value={value}
            getOptionLabel={getOptionLabel}
            onChange={handleChange}
            loading={loading}
            disabled={disabled}
            fullWidth={fullWidth}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                    // Ensure we always render a string
                    const tagLabel = typeof option === "string" ? option : option.tag || String(option);
                    return (
                        <Chip variant="outlined" label={tagLabel} {...getTagProps({ index })} key={index} />
                    );
                })
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    helperText={helperText}
                    variant={variant}
                    margin={margin}
                    error={error}
                    slotProps={
                        onOpenTagsDialog
                            ? {
                                  input: {
                                      ...params.InputProps,
                                      endAdornment: (
                                          <>
                                              {params.InputProps.endAdornment}
                                              <InputAdornment position="end">
                                                  <IconButton
                                                      onClick={onOpenTagsDialog}
                                                      edge="end"
                                                      aria-label="View available tags"
                                                      size="small"
                                                  >
                                                      <OpenInNewIcon fontSize="small" />
                                                  </IconButton>
                                              </InputAdornment>
                                          </>
                                      ),
                                  },
                              }
                            : undefined
                    }
                />
            )}
            sx={sx}
        />
    );
}
